import React, { memo } from 'react';
import { Pressable, View } from 'react-native';

import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  GuardianCircleMemberStatus,
  type GuardianCircleMember,
} from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';

import GuardianCircleStatusBadge, { getBadgeStatus } from './guardian-circle-status-badge';

const strings = STRINGS.guardianPlayer.guardianCircle;
const AVATAR_SIZE = 48;

type Props = {
  member: GuardianCircleMember;
  onPress: (member: GuardianCircleMember) => void;
  /** Tablet/web master-detail: highlight this row while its profile shows on the right. */
  selected?: boolean;
  isPrimaryGuardian: boolean;
  /** Creator-only override for the meta line, e.g. "· Created profile since Sep 2023"
   *  (from the player's `createdOn`). Falls back to the member's own meta label. */
  playerCreatedLabel?: string;
};

const GuardianCircleMemberRow = ({
  member,
  onPress,
  selected = false,
  isPrimaryGuardian,
  playerCreatedLabel,
}: Props) => {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const isActive = member.status === 'creator' || member.status === 'joined';

  // - Active members: always tappable (to view profile)
  // - Pending members (invitations): only tappable by owner AND must have an invitationCode
  const isPendingTappable = isPrimaryGuardian && !!member.invitationCode;
  const tappable = !member.isSelf && (isActive || isPendingTappable);
  // Pending members push a new screen even on tablet, so they always get a chevron.
  // Active members swap the right pane on tablet (no chevron), but get a chevron on every
  // mobile layout — phone and web alike.
  const showChevron = tappable && (!isActive || !isTablet);
  const badgeStatus = getBadgeStatus(member.status);
  const initial = member.name.trim().charAt(0).toUpperCase();
  const displayName = member.isSelf ? `${member.name} ${strings.you}` : member.name;
  const metaLabel =
    member.status === GuardianCircleMemberStatus.Creator && playerCreatedLabel
      ? playerCreatedLabel
      : member.metaLabel;

  const content = (
    <Stack direction="row" align="center" gap={scale(8)}>
      <Stack
        align="center"
        justify="center"
        width={scale(AVATAR_SIZE)}
        height={scale(AVATAR_SIZE)}
        borderRadius={scale(AVATAR_SIZE / 2)}
        borderWidth={selected ? scale(3) : 0}
        borderColor={selected ? colors['0'] : undefined}
        backgroundColor={isActive ? guardianColors['8'] : guardianColors['3']}
        overflow="hidden">
        {member.avatarUrl ? (
          <Image
            source={{ uri: member.avatarUrl }}
            width={scale(AVATAR_SIZE)}
            height={scale(AVATAR_SIZE)}
            circular
            resizeMode="cover"
          />
        ) : (
          <Text
            fontFamily={fontFamilies.montserrat}
            fontSize={scale(20)}
            lineHeight={scale(28)}
            fontWeight="700"
            style={isActive ? styles.initialActive : styles.initialInactive}>
            {initial}
          </Text>
        )}
      </Stack>

      <Stack flex={1} gap={scale(2)}>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
          numberOfLines={1}
          style={[styles.name, badgeStatus ? styles.nameInactive : null]}>
          {displayName}
        </Text>
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" numberOfLines={1}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={isActive ? styles.leadActive : styles.metaMuted}>
            {member.leadLabel}
          </Text>
          {metaLabel ? (
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.metaMuted}>
              {` ${metaLabel}`}
            </Text>
          ) : null}
        </Text>
      </Stack>

      {badgeStatus ? <GuardianCircleStatusBadge status={badgeStatus} /> : null}
      {showChevron && !isTablet ? (
        <SvgIcon name="IcChevronRight" size={scale(20)} color={guardianColors['6']} />
      ) : null}
    </Stack>
  );

  const rowStyle = [styles.row, isTablet ? styles.rowTablet : null];

  const divider = isTablet && badgeStatus ? <View style={styles.divider} /> : null;

  if (!tappable) {
    return (
      <>
        {divider}
        <Stack style={rowStyle}>{content}</Stack>
      </>
    );
  }

  return (
    <>
      {divider}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={displayName}
        accessibilityState={{ selected }}
        onPress={() => onPress(member)}
        style={({ pressed }) => [
          rowStyle,
          isTablet && selected ? styles.rowSelected : null,
          pressed ? styles.pressed : null,
        ]}>
        {content}
      </Pressable>
    </>
  );
};

const useStyles = createStyles(({ scale }) => ({
  row: {
    paddingVertical: scale(10),
  },
  rowTablet: {
    paddingLeft: scale(12),
    borderRadius: scale(12),
    borderCurve: 'continuous',
  },
  rowSelected: {
    backgroundColor: guardianColors['Main/10'],
  },
  pressed: {
    opacity: 0.6,
  },
  name: {
    color: guardianColors['10'],
  },
  nameInactive: {
    color: guardianColors['6'],
  },
  divider: {
    height: 1,
    backgroundColor: guardianColors['3'],
    marginVertical: scale(4),
  },
  leadActive: {
    color: guardianColors['Main/Main'],
  },
  metaMuted: {
    color: guardianColors['6'],
  },
  initialActive: {
    color: guardianColors['Main/Main'],
  },
  initialInactive: {
    color: guardianColors['5'],
  },
}));

export default memo(GuardianCircleMemberRow);
